import { Component } from 'preact';
import {
  GOLD, RADIUS, TEXT_PRIMARY, ACCENT_GOLD,
  CONTENT_BG_DEFAULT,
} from '../../lib/medievalTheme';
import ElementIcon from '../deckStats/ElementIcon';
import StoneButton from './StoneButton';
import { getActiveEvent } from '../../utils/game/engine/core';
import { isFoilFinish } from '../../utils/sorcery/foil';
import { foilZonesForCard } from '../../utils/sorcery/foilZones';
import { DEFAULT_FOIL_PARAMS } from '../../utils/sorcery/foilParams';
import FoilPreview from '../FoilPreview';
import { resolveLocalImageUrl } from '../../utils/localApi';
import { t } from '../../localization/t';

// Right-edge drawer for card-effect automation prompts.
//
// Surface vocabulary matches the music library popover
// (AudioMixerPopover.IconAction + OPAQUE_TEXTURED_PANEL_STYLE). Full
// stone+chisel+scratches+cracks+noise texture stack over a fully
// opaque dark base, with a layered multi-ring shadow that brightens
// on hover and recesses on press. Gold appears as accent only.
//
// The card preview already shows the rules text the player needs to
// decide on. There's no separate description chip in the prompt.
//
// Props:
//   prompt. Current pending prompt object or null. Shape:
//     {
//       id: string,
//       cardInstance: { id, cardId, imageUrl, isSite, name, ... },
//       cardName: string,
//       promptType: 'confirm' | 'choice' | 'draw' | 'reminder',
//       choices?: [{ label, value }],
//     }
//   onConfirm. (promptId, choiceValue?) => void
//   onCancel. (promptId) => void

const PORTRAIT_WIDTH_CQW  = 14;
const PORTRAIT_HEIGHT_CQW = 19;
const LANDSCAPE_WIDTH_CQW  = 19;
const LANDSCAPE_HEIGHT_CQW = 14;
const BUTTON_WIDTH_CQW  = 10;
const BUTTON_HEIGHT_CQW = 3;
const GAP_CQW = 0.55;
const HALO_PAD_X_CQW = 24;
const HALO_PAD_Y_CQW = 20;
const TRANSITION = 'cubic-bezier(0.22, 0.9, 0.3, 1)';

// Opaque dark wood/stone base under the texture stack. Matches
// OPAQUE_PANEL_BG from the music library.
const OPAQUE_BASE = '#0e0c08';

// Forged-stone button background. Layers (top → bottom):
//   • tex-stone. Surface grain
//   • tex-chisel. Carved edge tool marks
//   • tex-scratches. Surface wear
//   • tex-cracks. Fine fractures
//   • noise SVG. Fine grain
//   • soft vertical gradient. Top highlight, bottom recess
//   • opaque dark base color underneath
//
// CONTENT_BG_DEFAULT bundles all the texture layers; we prepend a
// tonal gradient for the button's lit/recessed feel and stack the
// opaque base color last so nothing is transparent.
const STONE_BG_IDLE = `linear-gradient(180deg, rgba(40,30,15,0.55) 0%, rgba(15,10,4,0.85) 100%), ${CONTENT_BG_DEFAULT}`;
const STONE_BG_HOVER = `linear-gradient(180deg, rgba(80,60,28,0.70) 0%, rgba(28,20,10,0.85) 100%), ${CONTENT_BG_DEFAULT}`;
const STONE_BG_PRESS = `linear-gradient(180deg, rgba(10,8,4,0.85) 0%, rgba(40,30,15,0.50) 100%), ${CONTENT_BG_DEFAULT}`;

// Music library blends its texture stack with overlay so the textures
// modulate the surface tint rather than sitting on top opaquely.
const STONE_BLEND = 'normal, soft-light, normal, normal, normal, normal';

const PRIMARY_TEXT = ACCENT_GOLD;
const SECONDARY_TEXT = 'rgba(212, 168, 67, 0.78)';

const SHADOW_PRIMARY_IDLE = [
  'inset 0 0.0625cqw 0 rgba(255, 235, 195, 0.22)',
  'inset 0 -0.0625cqw 0 rgba(0, 0, 0, 0.45)',
  'inset 0 0 1.5cqw rgba(0, 0, 0, 0.4)',
  '0 0 0 0.0625cqw rgba(232, 195, 110, 0.78)',
  '0 0 0 0.1875cqw rgba(0, 0, 0, 0.6)',
  '0 0 0 0.25cqw rgba(212, 168, 67, 0.28)',
  '0 0.4cqw 1cqw rgba(0, 0, 0, 0.6)',
  '0 0 1.1cqw rgba(232, 195, 110, 0.18)',
].join(', ');
const SHADOW_PRIMARY_HOVER = [
  'inset 0 0.0625cqw 0 rgba(255, 240, 200, 0.32)',
  'inset 0 -0.0625cqw 0 rgba(0, 0, 0, 0.45)',
  'inset 0 0 1.75cqw rgba(120, 80, 25, 0.20)',
  '0 0 0 0.0625cqw rgba(255, 220, 130, 1)',
  '0 0 0 0.1875cqw rgba(0, 0, 0, 0.6)',
  '0 0 0 0.25cqw rgba(232, 195, 110, 0.52)',
  '0 0.5cqw 1.25cqw rgba(0, 0, 0, 0.65)',
  '0 0 2cqw rgba(232, 195, 110, 0.4)',
].join(', ');
const SHADOW_PRIMARY_PRESS = [
  'inset 0 0.125cqw 0.25cqw rgba(0, 0, 0, 0.55)',
  'inset 0 -0.0625cqw 0 rgba(255, 235, 195, 0.12)',
  'inset 0 0 2cqw rgba(0, 0, 0, 0.45)',
  '0 0 0 0.0625cqw rgba(212, 168, 67, 0.7)',
  '0 0 0 0.1875cqw rgba(0, 0, 0, 0.6)',
  '0 0 0 0.25cqw rgba(180, 140, 60, 0.22)',
  '0 0.15cqw 0.4cqw rgba(0, 0, 0, 0.55)',
].join(', ');

const SHADOW_SECONDARY_IDLE = [
  'inset 0 0.0625cqw 0 rgba(255, 235, 195, 0.14)',
  'inset 0 -0.0625cqw 0 rgba(0, 0, 0, 0.45)',
  'inset 0 0 1.5cqw rgba(0, 0, 0, 0.5)',
  '0 0 0 0.0625cqw rgba(180, 140, 60, 0.5)',
  '0 0 0 0.1875cqw rgba(0, 0, 0, 0.55)',
  '0 0 0 0.25cqw rgba(180, 140, 60, 0.16)',
  '0 0.35cqw 0.9cqw rgba(0, 0, 0, 0.55)',
].join(', ');
const SHADOW_SECONDARY_HOVER = [
  'inset 0 0.0625cqw 0 rgba(255, 235, 195, 0.22)',
  'inset 0 -0.0625cqw 0 rgba(0, 0, 0, 0.45)',
  'inset 0 0 1.75cqw rgba(0, 0, 0, 0.45)',
  '0 0 0 0.0625cqw rgba(212, 168, 67, 0.78)',
  '0 0 0 0.1875cqw rgba(0, 0, 0, 0.6)',
  '0 0 0 0.25cqw rgba(212, 168, 67, 0.30)',
  '0 0.45cqw 1.1cqw rgba(0, 0, 0, 0.6)',
  '0 0 1.4cqw rgba(212, 168, 67, 0.18)',
].join(', ');
const SHADOW_SECONDARY_PRESS = [
  'inset 0 0.125cqw 0.25cqw rgba(0, 0, 0, 0.55)',
  'inset 0 -0.0625cqw 0 rgba(255, 235, 195, 0.08)',
  'inset 0 0 2cqw rgba(0, 0, 0, 0.5)',
  '0 0 0 0.0625cqw rgba(180, 140, 60, 0.45)',
  '0 0 0 0.1875cqw rgba(0, 0, 0, 0.6)',
  '0 0 0 0.25cqw rgba(180, 140, 60, 0.13)',
  '0 0.15cqw 0.4cqw rgba(0, 0, 0, 0.55)',
].join(', ');

const TEXT_SHADOW_DEEP = '0 1px 2px rgba(0, 0, 0, 0.85), 0 0 0.6cqw rgba(212, 168, 67, 0.16)';

const cap = (s) => s.charAt(0).toUpperCase() + s.slice(1);

// Prompt types resolved by clicking the board (a halo'd cell / unit),
// NOT a drawer button. These must leave the board interactive. Every
// other prompt type forces an in-drawer choice, so the board is blocked
// behind a backdrop until the player answers.
const BOARD_PICK_TYPES = new Set([
  'pick_caster',
  'pick_animist_spirit',
  'pick_cell',
  'pick_target',
]);

// The active storyline prompt IF it mounts this drawer's board-blocking
// backdrop, else null. Mirrors the mount conditions: board-pick prompts
// never block, and renderBody hides the drawer for a LONE
// draw_phase_choice (StartDrawOverlay owns that UI, board stays
// interactive). The hotkey layer needs this because while the backdrop
// is up the canvas receives no mousemove, so hoveredMesh freezes on
// whatever board card was under the cursor before the prompt opened and
// must not be trusted (valkenhall-qa#80). The DOM hand / unit-hand
// overlays render above the backdrop and stay live.
export function boardBlockingPrompt(prompts, serverChoice = null) {
  const active = getActiveEvent(prompts);
  if (!active) return null;
  if (active.revealNotice && serverChoice) return null;
  if (BOARD_PICK_TYPES.has(active.promptType)) return null;
  if (prompts.length === 1 && active.promptType === 'draw_phase_choice') return null;
  return active;
}

// The card a MODAL server choice (pendingServerChoice) displays,
// resolved against the local copies (own pile / mesh / hand) so it is
// the exact revealed card, orientation-independent. Shared by
// renderBody's server-choice drawer prop and the inspect hotkey: a
// modal server choice renders as a synthetic 'choice' prompt, which
// mounts the same board-blocking backdrop as a storyline choice, so the
// hotkey resolves the inspector against this card rather than the
// frozen hover state (cell / target picks render as board-pick prompts
// and leave the board, and its hover pipeline, live).
export function serverChoiceDisplayCard(host, pc) {
  const cardId = pc?.display?.cardId;
  if (!cardId) return null;
  return host._cardInstanceById(cardId)
    || (host.state.gameState?.piles || []).flatMap((p) => p.cards || []).find((c) => c.id === cardId)
    || [...(host.state.handCards || []), ...(host.state.handCards2 || [])].find((c) => c.id === cardId)
    || null;
}

// Local alias. Every PromptButton callsite below predates the
// shared module's StoneButton name. These dimensions are minimums;
// the shared button preserves padding as labels grow or wrap.
function PromptButton(props) {
  return (
    <StoneButton
      widthCqw={BUTTON_WIDTH_CQW}
      heightCqw={BUTTON_HEIGHT_CQW}
      {...props}
    />
  );
}

export default class EffectPromptDrawer extends Component {
  state = { snapshot: null };

  // v1.110. The drawer owns every prompt it receives. The parent
  // decides whether the start-phase draw routes through here or
  // through the pile-anchored StartDrawOverlay (the latter only when
  // the draw is alone in the storyline), so by the time a
  // draw_phase_choice lands on this prop it means the parent wants
  // the in-drawer rendering.
  _isOwnedPrompt(prompt) {
    return !!prompt;
  }

  componentDidUpdate(prevProps) {
    if (
      this._isOwnedPrompt(this.props.prompt)
      && this.props.prompt !== prevProps.prompt
    ) {
      clearTimeout(this._clearTimer);
      this.setState({ snapshot: this.props.prompt });
    } else if (!this.props.prompt && prevProps.prompt) {
      // Prompt dismissed. Keep the snapshot for the ~260ms slide-out,
      // then drop it so the drawer fully unmounts. Leaving it mounted
      // strands a position:fixed ghost translated 110% off the right
      // edge of the .app-canvas composited layer. Chromium then
      // intermittently keeps the extended layer bounds and paints the
      // overrun as a black strip, shoving the board left.
      clearTimeout(this._clearTimer);
      this._clearTimer = setTimeout(() => this.setState({ snapshot: null }), 320);
    }
  }

  componentDidMount() {
    if (this._isOwnedPrompt(this.props.prompt)) {
      this.setState({ snapshot: this.props.prompt });
    }
  }

  componentWillUnmount() {
    clearTimeout(this._clearTimer);
  }

  renderButtons(prompt) {
    const { onConfirm, onCancel } = this.props;
    const cancelBtn = (
      <PromptButton key="cancel" variant="secondary" onClick={() => onCancel(prompt.id)}>
        {t('Cancel')}
      </PromptButton>
    );

    const descriptionLabel = prompt.description ? (
      <div
        key="prompt-description"
        style={{
          color: PRIMARY_TEXT,
          fontSize: '1.2cqw',
          fontWeight: 600,
          textAlign: 'center',
          width: `${BUTTON_WIDTH_CQW}cqw`,
          textShadow: TEXT_SHADOW_DEEP,
          marginBottom: '0.25cqw',
          userSelect: 'none',
          pointerEvents: 'none',
        }}
      >
        {prompt.description}
      </div>
    ) : null;

    switch (prompt.promptType) {
      case 'confirm':
        return [
          descriptionLabel,
          <PromptButton key="confirm" variant="primary" autoFocus onClick={() => onConfirm(prompt.id)}>
            {t('Confirm')}
          </PromptButton>,
          cancelBtn,
        ].filter(Boolean);

      case 'draw':
        return [
          <PromptButton key="draw" variant="primary" autoFocus onClick={() => onConfirm(prompt.id)}>
            {t('Draw')}
          </PromptButton>,
          cancelBtn,
        ];

      // Spellcasting system: a "this card was played" notice. The caster
      // acknowledges to resolve their own spell to the cemetery; the
      // opponent acknowledges purely to clear the read-only notice. No
      // Cancel. A cast can't be un-played.
      case 'acknowledge':
        return [
          descriptionLabel,
          <PromptButton key="ack" variant="primary" autoFocus onClick={() => onConfirm(prompt.id)}>
            {t('Acknowledge')}
          </PromptButton>,
        ].filter(Boolean);

      case 'choice': {
        const choiceButtons = (prompt.choices || []).map((c) => {
          const isElement = ['air', 'earth', 'fire', 'water'].includes(c.value);
          return (
            <PromptButton key={c.value} variant="primary" onClick={() => onConfirm(prompt.id, c.value)}>
              {isElement ? <ElementIcon element={cap(c.value)} size={20} /> : null}
              <span>{c.label}</span>
            </PromptButton>
          );
        });
        return [...choiceButtons, cancelBtn];
      }

      case 'reminder':
        return [
          <PromptButton key="resolved" variant="primary" autoFocus onClick={() => onConfirm(prompt.id)}>
            {t('Mark resolved')}
          </PromptButton>,
          <PromptButton key="dismiss" variant="secondary" onClick={() => onCancel(prompt.id)}>
            {t('Dismiss')}
          </PromptButton>,
        ];

      case 'pick_caster':
        // Caster is picked by clicking a halo'd unit on the table. Show
        // the "Pick a Spellcaster…" label so the player knows what the
        // halo wants. Otherwise the drawer is just a lone Cancel button.
        return [descriptionLabel, cancelBtn].filter(Boolean);

      case 'pick_animist_spirit':
        // Animist arms "next magic → Spirit." The magic is chosen by
        // dragging a glowing hand card to a realm cell, so the drawer is
        // the Animist + an instruction label + a Cancel that disarms.
        return [descriptionLabel, cancelBtn].filter(Boolean);

      case 'pick_cell':
      case 'pick_target': {
        // Cell or target is picked by clicking a halo'd cell / card on
        // the board. The drawer shows the source card plus a non-
        // interactive label describing the action and a Cancel button.
        // Common pattern: pick_cell for Geomancer/Pathfinder/flood
        // abilities, pick_target for Savior-style "Ward target minion".
        // A MANDATORY server pick (noCancel) drops the button. The
        // decline intent would be a server no-op anyway.
        const fallback = prompt.promptType === 'pick_cell' ? t('Pick a cell') : t('Choose a target');
        return [
          <div
            key="pick-label"
            style={{
              color: PRIMARY_TEXT,
              fontSize: '1.2cqw',
              fontWeight: 600,
              textAlign: 'center',
              width: `${BUTTON_WIDTH_CQW}cqw`,
              textShadow: TEXT_SHADOW_DEEP,
              marginBottom: '0.25cqw',
              userSelect: 'none',
              pointerEvents: 'none',
            }}
          >
            {prompt.description || fallback}
          </div>,
          prompt.noCancel ? null : cancelBtn,
        ].filter(Boolean);
      }

      case 'draw_phase_choice': {
        // v1.50. Start Phase: "Draw a card from either your spellbook
        // or atlas." Two primary buttons (Draw a Spell / Draw a Site)
        // plus Cancel (sandbox escape; codex doesn't strictly allow
        // skipping the draw but the player can opt out for testing).
        const buttons = (prompt.choices || []).map((c) => (
          <PromptButton key={c.value} variant="primary" onClick={() => onConfirm(prompt.id, c.value)}>
            <span>{c.label}</span>
          </PromptButton>
        ));
        return [...buttons, cancelBtn];
      }

      case 'force_reveal_target': {
        // Searing Truth. The caster picks which player draws + reveals
        // two spells and takes the burn. Opponent / Yourself buttons;
        // Cancel defaults to the opponent (resolver handles it).
        const buttons = (prompt.choices || []).map((c) => (
          <PromptButton key={c.value} variant="primary" onClick={() => onConfirm(prompt.id, c.value)}>
            <span>{c.label}</span>
          </PromptButton>
        ));
        return [descriptionLabel, ...buttons, cancelBtn].filter(Boolean);
      }

      case 'interrogator_dodge': {
        // v1.103. Interrogator's "pay 3 life or let them draw" prompt.
        // Primary = pay, Cancel = let them draw (we treat cancel as the
        // decline path in the resolver).
        return [
          <PromptButton key="pay" variant="primary" autoFocus onClick={() => onConfirm(prompt.id, 'pay')}>
            {t('Pay 3 life')}
          </PromptButton>,
          <PromptButton key="decline" variant="secondary" onClick={() => onConfirm(prompt.id, 'decline')}>
            {t('Let them draw')}
          </PromptButton>,
        ];
      }

      case 'witch_curse_choice': {
        // v1.103. Witch's 3-option curse picker.
        const buttons = (prompt.choices || []).map((c) => (
          <PromptButton key={c.value} variant="primary" onClick={() => onConfirm(prompt.id, c.value)}>
            <span>{c.label}</span>
          </PromptButton>
        ));
        return [...buttons, cancelBtn];
      }

      case 'pile_end_swap': {
        // Riddle Sphinx. The opponent's yes/no on exchanging the
        // controller's top and bottom spells. Exchange / Leave buttons;
        // Cancel also declines.
        const buttons = (prompt.choices || []).map((c) => (
          <PromptButton key={c.value} variant="primary" onClick={() => onConfirm(prompt.id, c.value)}>
            <span>{c.label}</span>
          </PromptButton>
        ));
        return [descriptionLabel, ...buttons, cancelBtn].filter(Boolean);
      }

      case 'seer_scry_choice': {
        // v1.103. Seer's "look at your topmost site or spell."
        // Same button shape as draw_phase_choice; resolver opens the
        // ScryDialog on the chosen pile.
        const buttons = (prompt.choices || []).map((c) => (
          <PromptButton key={c.value} variant="primary" onClick={() => onConfirm(prompt.id, c.value)}>
            <span>{c.label}</span>
          </PromptButton>
        ));
        return [...buttons, cancelBtn];
      }

      case 'persecutor_choice': {
        // Persecutor's 2-mode picker: step toward Evil / brand an enemy.
        const buttons = (prompt.choices || []).map((c) => (
          <PromptButton key={c.value} variant="primary" onClick={() => onConfirm(prompt.id, c.value)}>
            <span>{c.label}</span>
          </PromptButton>
        ));
        return [...buttons, cancelBtn];
      }

      case 'pact_choice': {
        // Pact with the Devil's cost picker: sacrifice the caster / lose
        // half your life. The controller strips 'sacrifice' when the
        // caster is the player's Avatar, so render whatever choices remain.
        const buttons = (prompt.choices || []).map((c) => (
          <PromptButton key={c.value} variant="primary" onClick={() => onConfirm(prompt.id, c.value)}>
            <span>{c.label}</span>
          </PromptButton>
        ));
        return [...buttons, cancelBtn];
      }


      case 'placement_choice': {
        // Burrow / Submerge / Surface placement prompt fired when a
        // Minion with Burrowing or Submerge enters a cell that supports
        // its subsurface region. choiceValue is the region name.
        // 'surface', 'underground', or 'underwater'.
        const placementButtons = (prompt.choices || []).map((c) => (
          <PromptButton key={c.value} variant="primary" onClick={() => onConfirm(prompt.id, c.value)}>
            <span>{c.label}</span>
          </PromptButton>
        ));
        // No Cancel. The play is committed; one of the placements
        // must be picked. Default to Surface for safety if the prompt
        // ever ends up with no choices (shouldn't happen).
        if (placementButtons.length === 0) {
          return [
            <PromptButton key="surface" variant="primary" autoFocus onClick={() => onConfirm(prompt.id, 'surface')}>
              {t('Surface')}
            </PromptButton>,
          ];
        }
        return placementButtons;
      }

      case 'equip_choice': {
        // Artifact attachment prompt. A Monument is conjured atop its site
        // (codex "Casting Artifacts") and can never be carried or left
        // floating, so it gets a single placement action and no "Leave on
        // field" escape hatch. Carriable artifacts keep the Field-vs-Equip
        // choice: equip to a Minion / Avatar, or drop flat and uncontrolled
        // (manually attach later via the Ctrl+click flow).
        const isMonument = prompt.targetType === 'site';
        const buttons = [
          <PromptButton key="equip" variant="primary" autoFocus onClick={() => onConfirm(prompt.id, 'equip')}>
            {isMonument ? t('Place on site') : t('Equip to unit')}
          </PromptButton>,
        ];
        if (!isMonument) {
          buttons.push(
            <PromptButton key="field" variant="secondary" onClick={() => onConfirm(prompt.id, 'field')}>
              {t('Leave on field')}
            </PromptButton>,
          );
        }
        return buttons;
      }

      default:
        return [cancelBtn];
    }
  }

  // Sites render landscape (portrait-source image rotated 90° to fit).
  // Matches CardPreviewDrawer's pattern.
  renderCardImage(display) {
    const isSite = !!display.cardInstance?.isSite;
    const isFoil = isFoilFinish(display.cardInstance?.foiling);
    const cardW = isSite ? LANDSCAPE_WIDTH_CQW  : PORTRAIT_WIDTH_CQW;
    const cardH = isSite ? LANDSCAPE_HEIGHT_CQW : PORTRAIT_HEIGHT_CQW;
    // Same resolution every other card renderer applies (qa#144 precedent):
    // a desktop peer's /sorcery-images path is unloadable on Pages, and a
    // localhost-absolute URL from another client's port never loads on desktop.
    const imageUrl = resolveLocalImageUrl(display.cardInstance?.imageUrl);

    const wrapperStyle = {
      position: 'relative',
      flexShrink: 0,
      width: `${cardW}cqw`,
      height: `${cardH}cqw`,
      borderRadius: isSite ? RADIUS.cardLandscape : RADIUS.card,
      overflow: 'hidden',
      border: `1px solid ${GOLD} 0.55)`,
      boxShadow: `0 0.5cqw 1.25cqw rgba(0,0,0,0.75), 0 0 1.5cqw ${GOLD} 0.18), inset 0 0 0 1px rgba(0,0,0,0.4)`,
      backgroundColor: OPAQUE_BASE,
    };

    if (!imageUrl) {
      return (
        <div style={wrapperStyle}>
          <div
            style={{
              width: '100%', height: '100%', display: 'flex',
              alignItems: 'center', justifyContent: 'center',
              color: TEXT_PRIMARY, fontSize: '1cqw',
            }}
          >
            {display.cardName}
          </div>
        </div>
      );
    }

    if (isSite) {
      return (
        <div style={wrapperStyle}>
          <img
            src={imageUrl}
            alt={display.cardName}
            draggable={false}
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              width: `${cardH}cqw`,
              height: `${cardW}cqw`,
              transform: 'translate(-50%, -50%) rotate(90deg)',
              transformOrigin: 'center center',
              objectFit: 'cover',
              pointerEvents: 'none',
            }}
          />
          {isFoil ? (
            <FoilPreview
              imageUrl={imageUrl}
              zones={foilZonesForCard(display.cardInstance)}
              params={DEFAULT_FOIL_PARAMS}
              rotate90
            />
          ) : null}
        </div>
      );
    }

    return (
      <div style={wrapperStyle}>
        <img
          src={imageUrl}
          alt={display.cardName}
          draggable={false}
          style={{
            width: '100%', height: '100%',
            objectFit: 'cover',
            pointerEvents: 'none',
          }}
        />
        {isFoil ? (
          <FoilPreview
            imageUrl={imageUrl}
            zones={foilZonesForCard(display.cardInstance)}
            params={DEFAULT_FOIL_PARAMS}
          />
        ) : null}
      </div>
    );
  }

  render() {
    const { prompt } = this.props;
    const owned = this._isOwnedPrompt(prompt);
    const display = owned ? prompt : this.state.snapshot;
    const open = owned;
    if (!display) return null;

    const isSite = !!display.cardInstance?.isSite;
    const hasCard = !!display.cardInstance?.imageUrl;
    const cardW = isSite ? LANDSCAPE_WIDTH_CQW  : PORTRAIT_WIDTH_CQW;
    const cardH = isSite ? LANDSCAPE_HEIGHT_CQW : PORTRAIT_HEIGHT_CQW;

    const buttons = this.renderButtons(display);
    const buttonsH = buttons.length * BUTTON_HEIGHT_CQW
      + Math.max(0, buttons.length - 1) * GAP_CQW;

    // Halo wraps the dialog's content box (buttons column + optional
    // card) with symmetric padding, so the radial darkening centers
    // on whatever is actually visible. The card + buttons belong
    // together visually, so when a card is present the halo spans
    // both and sits in the middle of the combined group rather than
    // hanging off the buttons.
    const DIALOG_PAD_RIGHT_CQW = 1.5;
    const NO_CARD_HALO_PAD_X_CQW = 10;
    const NO_CARD_HALO_PAD_Y_CQW = 6;
    const contentW = hasCard
      ? BUTTON_WIDTH_CQW + GAP_CQW * 2 + cardW
      : BUTTON_WIDTH_CQW;
    const contentH = hasCard ? Math.max(cardH, buttonsH) : buttonsH;
    const haloPadX = hasCard ? HALO_PAD_X_CQW : NO_CARD_HALO_PAD_X_CQW;
    const haloPadY = hasCard ? HALO_PAD_Y_CQW : NO_CARD_HALO_PAD_Y_CQW;
    const haloW = contentW + haloPadX;
    const haloH = contentH + haloPadY;
    const haloRightCqw = DIALOG_PAD_RIGHT_CQW - haloPadX / 2;

    // Block board interaction while a choice-requiring prompt is open:
    // a full-canvas backdrop swallows clicks before they reach the WebGL
    // canvas (which binds its own mousedown listener). Board-pick prompts
    // are exempt. They resolve by clicking the halo'd board target.
    const blockBoard = open && !BOARD_PICK_TYPES.has(display.promptType);

    return (
      <>
        {blockBoard ? (
          <div
            aria-hidden="true"
            className="fixed inset-0"
            style={{ zIndex: 58, cursor: 'not-allowed' }}
          />
        ) : null}

        {/* Deep elliptical vignette around the prompt. */}
        <div
          aria-hidden="true"
          className="fixed pointer-events-none"
          style={{
            right: `${haloRightCqw}cqw`,
            top: '50%',
            transform: `translateY(-50%) ${open ? 'scale(1)' : 'scale(0.92)'}`,
            width: `${haloW}cqw`,
            height: `${haloH}cqw`,
            background: 'radial-gradient(ellipse closest-side at center, rgba(0,0,0,0.94) 0%, rgba(0,0,0,0.84) 38%, rgba(0,0,0,0.58) 62%, rgba(0,0,0,0.26) 82%, transparent 100%)',
            opacity: open ? 1 : 0,
            transition: `opacity 240ms ${TRANSITION}, transform 260ms ${TRANSITION}`,
            zIndex: 59,
          }}
        />

        <div
          role="dialog"
          aria-label={t('Card effect: {name}', { name: display.cardName })}
          style={{
            position: 'fixed',
            right: 0,
            top: '50%',
            transform: `translate(${open ? '0' : '110%'}, -50%)`,
            opacity: open ? 1 : 0,
            transition: `transform 260ms ${TRANSITION}, opacity 220ms ${TRANSITION}`,
            pointerEvents: open ? 'auto' : 'none',
            zIndex: 60,
            padding: `2cqw ${DIALOG_PAD_RIGHT_CQW}cqw 2cqw 4cqw`,
            boxSizing: 'border-box',
            maxWidth: '100%',
            maxHeight: 'calc(100% - 2cqw)',
            overflowY: 'auto',
            display: 'flex',
            alignItems: 'safe center',
            gap: `${GAP_CQW * 2}cqw`,
          }}
        >
          <div
            style={{
              position: 'relative',
              display: 'flex',
              flexDirection: 'column',
              gap: `${GAP_CQW}cqw`,
              alignItems: 'flex-end',
              width: 'max-content',
              maxWidth: '18cqw',
              minWidth: 0,
            }}
          >
            {buttons}
          </div>

          {hasCard ? this.renderCardImage(display) : null}
        </div>
      </>
    );
  }
}
